import type { useAuth } from '@clerk/clerk-react';
import axios, { type AxiosRequestConfig } from 'axios';
import type { History } from 'history';
import { useCallback } from 'react';
import useAuthentication from '~/hooks/use-authentication';

type GetToken = ReturnType<typeof useAuth>['getToken'];

const responseInterceptors: number[] = [];

const api = axios.create({
  baseURL: process.env.API_URL,
});

api.defaults.headers.common['App-Release'] = process.env.RELEASE?.substr(0, 7);

function setOrganizationHeaders(organizationId: string): void {
  api.defaults.headers.common['x-onomondo-org-id'] = organizationId;
}

function setAuthenticationHeaders(token: string): void {
  api.defaults.headers.common.authorization = token;
}

function unsetAuthenticationHeaders(): void {
  api.defaults.headers.common.authorization = undefined;
  api.defaults.headers.common['x-onomondo-org-id'] = undefined;
}

function setInterceptors(history: History, getToken: GetToken): void {
  api.interceptors.request.use(async config => {
    const token = await getToken({ template: process.env.CLERK_DEFAULT_TOKEN_TEMPLATE });
    if (token) {
      config.headers = config.headers ?? {};
      config.headers.authorization = token;
    }
    return config;
  });
  responseInterceptors.push(
    api.interceptors.response.use(undefined, error => {
      const isUnauthorized = error.response && error.response.status === 401;
      if (isUnauthorized) {
        history.push('/logout');
      }
      return Promise.reject(error);
    }),
  );
}

function unsetInterceptors(): void {
  for (const interceptor of responseInterceptors) {
    api.interceptors.response.eject(interceptor);
  }
}

function useConfig(organizationId?: string | number | null): (config?: AxiosRequestConfig) => AxiosRequestConfig {
  const { isViewingAsAssumedOrganization } = useAuthentication();
  return useCallback(
    (config: AxiosRequestConfig = {}) => {
      const newConfig = { ...config };
      if (organizationId) {
        newConfig.headers = Object.assign(
          { organization_id: organizationId, assume_org: !!isViewingAsAssumedOrganization },
          newConfig.headers,
        );
      }
      return newConfig;
    },
    [isViewingAsAssumedOrganization, organizationId],
  );
}

export {
  setAuthenticationHeaders,
  setInterceptors,
  setOrganizationHeaders,
  unsetAuthenticationHeaders,
  unsetInterceptors,
  useConfig,
};

export default api;
